// src/services/users.ts
import axios from '../axiosInstance';

import type { User, Account, Role } from '../types/user';

interface AllUsersAndAccountsResponse {
  dpUsers: User[];
  accounts: Account[];
  users: User[];
}

interface CreateUser {
  firstName: string;
  lastName: string;
  email: string;
  role: Role;
  phone?: string;
  addToAllSampleAccounts?: boolean;
  accountIds?: string[];
  isDev?: boolean;
}

export interface UpdateUser {
  email: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  role?: Role;
  accountIds?: string[];
  isDev?: boolean;
}

/**
 * Fetch all users in the account.
 */
export const getUsers = async (): Promise<User[]> => {
  const { data } = await axios.get<User[]>('/admin/users');
  return data;
};

/**
 * List all accounts — superadmin gets all, regular users get only theirs.
 */
export const listAccounts = async (): Promise<Account[]> => {
  const { data } = await axios.get<Account[]>('/admin/accounts');
  return data;
};

/**
 * List all users and accounts — only for superadmin
 */
export const listUsersAndAccounts = async (): Promise<{ accounts: Account[]; users: User[] }> => {
  const { data } = await axios.get<{ accounts: Account[]; users: User[] }>(
    `/admin/users-and-accounts`,
  );
  return data;
};

/**
 * Switch to a different account (if the user has access to it).
 * @param accountId The ID of the account to switch to.
 */
export const switchAccounts = async (accountId: string): Promise<User> => {
  const { data } = await axios.post<User>(
    `/admin/switch-accounts?accountId=${encodeURIComponent(accountId)}`,
  );
  return data;
};

/**
 * Switch to a different account (if the user has access to it).
 * @param accountId The ID of the account to switch to.
 */
export const impersonateUser = async (
  userEmail: string,
  accountId: string,
): Promise<{ token: string }> => {
  const { data } = await axios.post<{ token: string }>(
    `/admin/impersonate?userEmail=${encodeURIComponent(userEmail)}&accountId=${encodeURIComponent(accountId)}`,
  );
  return data;
};

export const getRetailAccessToken = async (): Promise<{ token: string }> => {
  const { data } = await axios.get<{ token: string }>(`/admin/get-retail-external-access-token`);
  return data;
};

/**
 * Gets the current status of JWT lifetime validation
 */
export const getValidateLifetimeJwt = async (): Promise<boolean> => {
  const { data } = await axios.get<{ value: string }>('/admin/validate-lifetime-jwt');
  return data.value === 'true';
};

/**
 * Toggles the JWT lifetime validation
 * @param value "true" or "false" as strings per API requirement
 */
export const toggleValidateLifetimeJwt = async (value: boolean): Promise<void> => {
  const stringValue = value ? 'true' : 'false';
  await axios.post(`/admin/validate-lifetime-jwt?newValue=${stringValue}`);
};

/**
 * Fetch all users and accounts data (dpUsers for prefill, accounts list, and existing users).
 */
export const getAllUsersAndAccounts = async (skipDialpad: boolean = false): Promise<AllUsersAndAccountsResponse> => {
  const { data } = await axios.get<AllUsersAndAccountsResponse>(`/admin/all-users-and-accounts?skipDialpad=${skipDialpad}`);
  return data;
};

/**
 * Create a new user and add them to specified accounts.
 */
export const createUser = async (userData: CreateUser): Promise<User> => {
  const { data } = await axios.post<User>('/admin/users', userData);
  return data;
};

/**
 * Update an existing user's details and account access.
 * @param email The email of the user to update (used as identifier).
 * @param updates The fields to update.
 */
export const updateUser = async (updates: UpdateUser): Promise<User> => {
  const { data } = await axios.patch<User>('/admin/users', updates);
  return data;
};

/**
 * Delete a user by email.
 * @param email The email of the user to delete.
 */
export const deleteUser = async (email: string): Promise<void> => {
  await axios.delete(`/admin/users/${encodeURIComponent(email)}`);
};
